/**
 * Plugin Capability Enforcement — Frontend
 *
 * Provides capability-aware wrappers for all plugin-injected APIs.
 * When a plugin lacks a required capability, the wrapped API method
 * throws a `CapabilityDeniedError` and logs the denial for DevTools.
 *
 * The enforcement layer sits between the PluginLoader and the raw
 * interaction APIs created by `host-ui-api.ts`. It wraps each API
 * surface with capability checks before delegating to the real
 * implementation.
 *
 * @wave 22.13.6
 * @wave 11AU.2 — Strict enforcement: no "allow all" fallback for missing capabilities
 */

import type {
  HostUIAPI,
  PluginActionsAPI,
  PluginEventsAPI,
  ContentSyncAPI,
  ModalRequest,
  ModalResult,
  DeferredBody,
  DeferredActionResponse,
  InteractionPluginEventHandler as PluginEventHandler,
  ContentUpdateHandler,
  ContentConflictHandler,
  ContentVersion,
  PublishResult,
  ConflictStrategy,
  CapabilityToken,
  CapabilityDenial,
  ResolvedCapabilities,
  ManifestCapabilityDeclaration,
} from '@neuro/plugin-sdk/types';
import {
  CapabilityDeniedError,
  resolveCapabilities,
  hasCapability,
  ALL_CAPABILITY_TOKENS,
} from '@neuro/plugin-sdk/types';
import { invocationLog } from './interaction-invocation-log';

// ────────────────────────────────────────────────────────
// Capability Store (per-plugin, frontend-side)
// ────────────────────────────────────────────────────────

/**
 * Frontend capability store.
 *
 * Populated by the PluginLoader when it receives the manifest from
 * the backend. The enforcement wrappers consult this store for every
 * API call.
 */
const pluginCapabilities = new Map<string, ResolvedCapabilities>();
const pluginDenials = new Map<string, CapabilityDenial[]>();
const MAX_DENIALS_PER_PLUGIN = 100;

/**
 * Register resolved capabilities for a plugin.
 * Called by PluginLoader during plugin initialisation.
 *
 * @wave 11AU.2 — Always registers a capability set. When the declaration
 *                is missing or invalid, an empty (deny-all) set is stored
 *                to ensure the plugin is never left unregistered.
 */
export function registerPluginCapabilities(
  pluginId: string,
  declaration: ManifestCapabilityDeclaration | undefined | null,
): ResolvedCapabilities | null {
  const result = resolveCapabilities(declaration);
  if (result.valid && result.resolved) {
    pluginCapabilities.set(pluginId, result.resolved);
    return result.resolved;
  }
  // Wave 11AU.2: Even on failure, register an empty set so the plugin
  // is never left unregistered (which previously bypassed enforcement).
  const empty: ResolvedCapabilities = {
    granted: new Set(),
    declared: new Set(),
    autoGranted: new Set(),
  };
  pluginCapabilities.set(pluginId, empty);
  return null;
}

/**
 * Clear capabilities for a plugin (on unload / hot-reload).
 */
export function clearPluginCapabilities(pluginId: string): void {
  pluginCapabilities.delete(pluginId);
  pluginDenials.delete(pluginId);
}

/**
 * Test-only helper: grant every capability token to a plugin.
 *
 * Tests that exercise plugin-registered navigation entries, page actions,
 * etc. need the plugin to pass `isNavigationEntryCapabilityHealthy`. Strict
 * enforcement (Wave 11AU.2) filters out plugins with no registered
 * capabilities, so tests must register a permissive set for any plugin ID
 * they use. This helper makes that one-liner.
 *
 * @internal — test infrastructure only.
 */
export function __grantAllCapabilitiesForTests(pluginId: string): void {
  const all = new Set<CapabilityToken>(ALL_CAPABILITY_TOKENS);
  pluginCapabilities.set(pluginId, {
    granted: all,
    declared: all,
    autoGranted: new Set(),
  });
}

/**
 * Check if a plugin has a specific capability.
 *
 * @wave 11AU.2 — Strict: returns false when no capabilities are registered
 *                (no implicit "allow all" fallback).
 */
export function pluginHasCapability(pluginId: string, capability: CapabilityToken): boolean {
  const resolved = pluginCapabilities.get(pluginId);
  if (!resolved) return false;
  return hasCapability(resolved, capability);
}

/**
 * Get all denials for a plugin (for DevTools).
 */
export function getPluginDenials(pluginId: string): CapabilityDenial[] {
  return pluginDenials.get(pluginId) ?? [];
}

/**
 * Get all registered capabilities (for DevTools).
 */
export function getAllPluginCapabilities(): Map<string, ResolvedCapabilities> {
  return new Map(pluginCapabilities);
}

// ── Internal: record a denial ──

function recordDenial(pluginId: string, capability: CapabilityToken, api: string): void {
  const denials = pluginDenials.get(pluginId) ?? [];
  denials.push({
    pluginId,
    capability,
    api,
    timestamp: new Date().toISOString(),
  });
  while (denials.length > MAX_DENIALS_PER_PLUGIN) {
    denials.shift();
  }
  pluginDenials.set(pluginId, denials);
}

// ── Internal: check-and-throw ──

function assertCapability(pluginId: string, capability: CapabilityToken, api: string): void {
  if (!pluginHasCapability(pluginId, capability)) {
    recordDenial(pluginId, capability, api);

    // Log for DevTools
    invocationLog.append({
      kind: 'capability-denied',
      pluginId,
      detail: { capability, api },
    });

    console.warn(
      `[CapabilityEnforcement] Plugin "${pluginId}" denied "${api}" — ` +
      `requires capability "${capability}"`,
    );

    throw new CapabilityDeniedError(pluginId, capability);
  }
}

// ────────────────────────────────────────────────────────
// Enforcement Wrappers
// ────────────────────────────────────────────────────────

/**
 * Wrap a HostUIAPI with capability enforcement.
 *
 * Requires: `ui.modal` for openModal.
 */
export function enforceUICapabilities(
  pluginId: string,
  raw: HostUIAPI,
): HostUIAPI {
  return {
    openModal(request: ModalRequest): Promise<ModalResult> {
      assertCapability(pluginId, 'ui.modal', 'ui.openModal');
      return raw.openModal(request);
    },
    closeModal(result: ModalResult): void {
      // closeModal doesn't require separate capability — if they opened it, they can close it
      raw.closeModal(result);
    },
  };
}

/**
 * Wrap a PluginActionsAPI with capability enforcement.
 *
 * Requires:
 *   `actions.register` for register()
 *   `actions.invoke`   for defer/resolve/reject
 */
export function enforceActionCapabilities(
  pluginId: string,
  raw: PluginActionsAPI,
): PluginActionsAPI {
  return {
    register(name: string, handler: (...args: unknown[]) => unknown): void {
      assertCapability(pluginId, 'actions.register', 'actions.register');
      raw.register(name, handler);
    },

    defer<T = unknown>(
      body: DeferredBody<T>,
      options?: { label?: string; timeoutMs?: number },
    ): DeferredActionResponse {
      assertCapability(pluginId, 'actions.invoke', 'actions.defer');
      return raw.defer(body, options);
    },

    resolve<T = unknown>(actionId: string, result: T): void {
      assertCapability(pluginId, 'actions.invoke', 'actions.resolve');
      raw.resolve(actionId, result);
    },

    reject(actionId: string, reason: string): void {
      assertCapability(pluginId, 'actions.invoke', 'actions.reject');
      raw.reject(actionId, reason);
    },
  };
}

/**
 * Wrap a PluginEventsAPI with capability enforcement.
 *
 * Requires:
 *   `events.emit`      for emit()
 *   `events.subscribe`  for on()
 */
export function enforceEventCapabilities(
  pluginId: string,
  raw: PluginEventsAPI,
): PluginEventsAPI {
  return {
    emit(targetPluginId: string, eventName: string, payload: unknown): void {
      assertCapability(pluginId, 'events.emit', 'events.emit');
      raw.emit(targetPluginId, eventName, payload);
    },

    on(
      sourcePluginId: string,
      eventName: string,
      handler: PluginEventHandler,
    ): () => void {
      assertCapability(pluginId, 'events.subscribe', 'events.on');
      return raw.on(sourcePluginId, eventName, handler);
    },

    offAll(subscriberPluginId: string): void {
      // offAll doesn't require separate capability — cleanup is always allowed
      raw.offAll(subscriberPluginId);
    },
  };
}

/**
 * Wrap a ContentSyncAPI with capability enforcement.
 *
 * Requires:
 *   `sync.publish`   for publish()
 *   `sync.subscribe` for subscribe() / subscribeConflicts()
 */
export function enforceContentSyncCapabilities(
  pluginId: string,
  raw: ContentSyncAPI,
): ContentSyncAPI {
  return {
    publish(
      entityId: string,
      content: string,
      options?: {
        baseVersion?: number;
        conflictStrategy?: ConflictStrategy;
        mergeFn?: (existing: string, incoming: string) => string;
      },
    ): PublishResult {
      assertCapability(pluginId, 'sync.publish', 'contentSync.publish');
      return raw.publish(entityId, content, options);
    },

    subscribe(entityId: string, handler: ContentUpdateHandler): () => void {
      assertCapability(pluginId, 'sync.subscribe', 'contentSync.subscribe');
      return raw.subscribe(entityId, handler);
    },

    subscribeConflicts(entityId: string, handler: ContentConflictHandler): () => void {
      assertCapability(pluginId, 'sync.subscribe', 'contentSync.subscribeConflicts');
      return raw.subscribeConflicts(entityId, handler);
    },

    getLatest(entityId: string): ContentVersion | undefined {
      // Read-only access — no specific capability required beyond basic sync
      return raw.getLatest(entityId);
    },

    getHistory(entityId: string): ContentVersion[] {
      // Read-only access
      return raw.getHistory(entityId);
    },
  };
}

// ────────────────────────────────────────────────────────
// Combined Enforcement Wrapper
// ────────────────────────────────────────────────────────

export interface EnforcedPluginAPIs {
  ui: HostUIAPI;
  actions: PluginActionsAPI;
  events: PluginEventsAPI;
  contentSync: ContentSyncAPI;
}

/**
 * Wrap all interaction APIs for a plugin with capability enforcement.
 *
 * @wave 11AU.2 — Strict: all plugins are enforced. No pass-through
 *                for plugins without capability declarations.
 */
export function enforcePluginCapabilities(
  pluginId: string,
  apis: {
    ui: HostUIAPI;
    actions: PluginActionsAPI;
    events: PluginEventsAPI;
    contentSync: ContentSyncAPI;
  },
): EnforcedPluginAPIs {
  return {
    ui: enforceUICapabilities(pluginId, apis.ui),
    actions: enforceActionCapabilities(pluginId, apis.actions),
    events: enforceEventCapabilities(pluginId, apis.events),
    contentSync: enforceContentSyncCapabilities(pluginId, apis.contentSync),
  };
}
